import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';
import apiService from '../../../services/api';

/**
 * Python 스크립트 미리보기 컴포넌트
 * GCS 버킷에 저장된 Python 스크립트를 표시합니다.
 */
const PythonScriptPreview = ({
  tenantCode,
  filePromptMapping,
  files
}) => {
  const [scriptsData, setScriptsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadScripts();
  }, [tenantCode, filePromptMapping]);

  const loadScripts = async () => {
    if (!tenantCode || Object.keys(filePromptMapping).length === 0) {
      setScriptsData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const results = [];
      const uniqueFileIds = Object.keys(filePromptMapping);

      console.log('[PythonScriptPreview] filePromptMapping:', filePromptMapping);

      for (const fileId of uniqueFileIds) {
        const file = files.find(f => f.file_id === fileId);
        if (!file) continue;

        const selectedPrompts = filePromptMapping[fileId] || [];
        console.log(`[PythonScriptPreview] File ${fileId} has ${selectedPrompts.length} prompts:`, selectedPrompts);

        // 각 파일에 매핑된 모든 프롬프트의 Python 스크립트를 가져옴
        for (const promptId of selectedPrompts) {
          try {
            // promptId는 문자열 (예: "OPERATION_RATE_BANPO")
            const response = await apiService.getPythonScript(tenantCode, promptId);

            if (response.success && response.data) {
              results.push({
                fileId: fileId,
                fileName: file.displayName || file.standard_file_name,
                promptId: response.data.prompt_id,
                promptName: response.data.prompt_name,
                pythonFile: response.data.python_file,
                gcsPath: response.data.gcs_path,
                content: response.data.content,
                size: response.data.size
              });
            }
          } catch (err) {
            console.warn(`Python script not found for ${promptId}:`, err);
            // Continue with other prompts
          }
        }
      }

      setScriptsData(results);
    } catch (err) {
      console.error('Python 스크립트 로딩 실패:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Python 스크립트 로딩 중...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
        <Alert severity="error">
          Python 스크립트 로딩 실패: {error}
        </Alert>
      </Paper>
    );
  }

  if (scriptsData.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CodeIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            파일을 선택하면
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Python 스크립트가 여기에 표시됩니다
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Python 스크립트 미리보기
        </Typography>
        <Typography variant="caption" color="text.secondary">
          총 {scriptsData.length}개 스크립트
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {scriptsData.map((item, index) => (
          <Paper
            key={`${item.fileId}-${item.promptId}`}
            variant="outlined"
            sx={{ p: 2, bgcolor: 'background.default' }}
          >
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {index + 1}. {item.promptName || item.promptId}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  파일: {item.fileName} (ID: {item.fileId})
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  프롬프트 ID: {item.promptId}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  경로: {item.gcsPath}
                </Typography>
              </Box>
              <Chip
                label={`${(item.size / 1024).toFixed(1)} KB`}
                size="small"
                color="info"
              />
            </Box>

            <Box
              sx={{
                p: 2,
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                borderRadius: 1,
                fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                fontSize: '0.8rem',
                lineHeight: 1.5,
                whiteSpace: 'pre',
                overflow: 'auto',
                maxHeight: 400,
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: '#2d2d2d'
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: '#555',
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: '#777'
                  }
                }
              }}
            >
              {item.content}
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};

export default PythonScriptPreview;
